import './ExportAthletesPage.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import PageTopNav from '../components/common/PageTopNav/PageTopNav';
import { usePostHog } from '@posthog/react';
import { scoringApp } from '../firebase';
import { AthleteWithId } from '../types/athlete';
import { copyTextToClipboard } from '../utils/clipboard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; teams: string[]; athletes: AthleteWithId[] };

/** Outcome of building the roster text for the selected team. */
type ExportResult =
  | { kind: 'no-team' }
  | { kind: 'no-athletes' }
  | { kind: 'no-fields' }
  | { kind: 'text'; text: string };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ExportAthletesPage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const db = getFirestore(scoringApp);

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [showNames, setShowNames] = useState(true);
  const [showEmails, setShowEmails] = useState(false);
  const [showNumbers, setShowNumbers] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  // useEffect is appropriate here: fetching data from Firestore (external system).
  useEffect(() => {
    if (!tryoutId) {
      setLoadState({ status: 'error', message: 'Invalid tryout.' });
      return;
    }

    let cancelled = false;

    async function load(): Promise<void> {
      const [tryoutSnap, athletesSnap] = await Promise.all([
        getDoc(doc(db, 'tryouts', tryoutId!)),
        getDocs(collection(db, 'tryouts', tryoutId!, 'athletes')),
      ]);

      if (cancelled) return;

      if (!tryoutSnap.exists()) {
        setLoadState({ status: 'error', message: 'Tryout not found.' });
        return;
      }

      const data = tryoutSnap.data() as Record<string, unknown>;
      const teams = Array.isArray(data.teams) ? (data.teams as string[]) : [];

      const athletes: AthleteWithId[] = [];
      athletesSnap.forEach(d => {
        athletes.push({ id: d.id, ...d.data() } as AthleteWithId);
      });

      setLoadState({ status: 'ready', teams, athletes });
    }

    load().catch((err: unknown) => {
      if (!cancelled) {
        const detail = err instanceof Error ? ` (${err.message})` : '';
        setLoadState({ status: 'error', message: `Failed to load.${detail}` });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [db, tryoutId]);

  // Derived — compute during render, no effect needed.
  const exportResult = useMemo<ExportResult>(() => {
    if (loadState.status !== 'ready' || selectedTeam === null) return { kind: 'no-team' };

    const sorted = loadState.athletes
      .filter(a => a.teamPlacement === selectedTeam)
      .sort((a, b) => {
        const na = parseInt(a.number, 10);
        const nb = parseInt(b.number, 10);
        if (isNaN(na) && isNaN(nb)) return 0;
        if (isNaN(na)) return 1;
        if (isNaN(nb)) return -1;
        return na - nb;
      });

    if (sorted.length === 0) return { kind: 'no-athletes' };
    if (!showNumbers && !showNames && !showEmails) return { kind: 'no-fields' };

    const lines = sorted
      .map(a => {
        const parts: string[] = [];
        if (showNumbers) parts.push(`#${a.number}`);
        if (showNames) parts.push(a.name);
        if (showEmails && a.email) parts.push(a.email);
        return parts.join(' - ');
      })
      .filter(Boolean);

    // Every athlete dropped out: only "Show Emails" is on and nobody has an email.
    if (lines.length === 0) return { kind: 'no-fields' };

    return { kind: 'text', text: lines.join('\n') };
  }, [loadState, selectedTeam, showEmails, showNames, showNumbers]);

  const handleCopy = useCallback((): void => {
    if (exportResult.kind !== 'text') return;
    void copyTextToClipboard(exportResult.text).then(() => {
      posthog?.capture('team_roster_exported', { tryout_id: tryoutId, team_name: selectedTeam });
      setCopySuccess(true);
      if (copyTimeoutRef.current != null) window.clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopySuccess(false);
        copyTimeoutRef.current = null;
      }, 2000);
    });
  }, [exportResult, posthog, selectedTeam, tryoutId]);

  const handleBack = useCallback((): void => {
    navigate(`/tryout/${tryoutId}`);
  }, [navigate, tryoutId]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loadState.status === 'loading') {
    return (
      <div className="export-athletes-shell">
        <PageTopNav
          title="Export Athletes"
          onBack={handleBack}
          backAriaLabel="Back to tryout"
        />
        <div className="export-athletes-content">
          <div className="export-athletes-skeleton-row">
            <div className="export-athletes-skeleton export-athletes-skeleton--chip" />
            <div className="export-athletes-skeleton export-athletes-skeleton--chip" />
            <div className="export-athletes-skeleton export-athletes-skeleton--chip" />
          </div>
        </div>
      </div>
    );
  }

  if (loadState.status === 'error') {
    return (
      <div className="export-athletes-shell">
        <PageTopNav
          title="Export Athletes"
          onBack={handleBack}
          backAriaLabel="Back to tryout"
        />
        <div className="export-athletes-content">
          <p className="export-athletes-error">{loadState.message}</p>
        </div>
      </div>
    );
  }

  const { teams } = loadState;

  return (
    <div className="export-athletes-shell">
      <PageTopNav
        title="Export Athletes"
        onBack={handleBack}
        backAriaLabel="Back to tryout"
      />

      <div className="export-athletes-content">
        <section className="export-athletes-section">
          <p className="export-athletes-label">Team</p>
          <div className="export-athletes-team-row">
            {teams.map(team => (
              <button
                key={team}
                type="button"
                className={[
                  'export-athletes-team-btn',
                  selectedTeam === team ? 'export-athletes-team-btn--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setSelectedTeam(prev => prev === team ? null : team)}
              >
                {team}
              </button>
            ))}
          </div>
        </section>

        <section className="export-athletes-section">
          <p className="export-athletes-label">Display</p>
          <div className="export-athletes-checkbox-row">
            <label className="export-athletes-checkbox-label">
              <input
                type="checkbox"
                checked={showNumbers}
                onChange={e => setShowNumbers(e.target.checked)}
              />
              Show Numbers
            </label>
            <label className="export-athletes-checkbox-label">
              <input
                type="checkbox"
                checked={showNames}
                onChange={e => setShowNames(e.target.checked)}
              />
              Show Names
            </label>
            <label className="export-athletes-checkbox-label">
              <input
                type="checkbox"
                checked={showEmails}
                onChange={e => setShowEmails(e.target.checked)}
              />
              Show Emails
            </label>
          </div>
        </section>

        {selectedTeam !== null && exportResult.kind !== 'no-team' && (
          <section className="export-athletes-section">
            <div className="export-athletes-result-header">
              <p className="export-athletes-label">{selectedTeam}</p>
              <button
                type="button"
                className="export-athletes-copy-btn"
                onClick={handleCopy}
                disabled={exportResult.kind !== 'text'}
              >
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            </div>
            {exportResult.kind === 'no-athletes' && (
              <p className="export-athletes-empty">No athletes placed on this team.</p>
            )}
            {exportResult.kind === 'no-fields' && (
              <p className="export-athletes-empty">Pick at least one field to display.</p>
            )}
            {exportResult.kind === 'text' && (
              <pre className="export-athletes-pre">{exportResult.text}</pre>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ExportAthletesPage;
